import { useEffect, useRef } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { getUrl } from "../../../../services/utils";
import { getCMSLink } from "../../../../services/cmsLogin";
import Colors from "../../../../themes/Colors";

const langToReport = (lang) => {
  if (lang === "es") return "esLA";
  if (lang === "es_ar") return "es";
  return lang || "en";
};

const useCampaignDetailActions = ({
  campaign,
  token,
  history,
  getProposalRequest,
  proposalToken,
  loadingProposal,
  getReportRequest,
  reportToken,
  loadingReport,
  editCampaign,
}) => {
  const { t, i18n } = useTranslation();
  const proposalPending = useRef(false);
  const reportPending = useRef(false);
  const prevLoadingProposal = useRef(loadingProposal);
  const prevLoadingReport = useRef(loadingReport);

  useEffect(() => {
    if (
      prevLoadingProposal.current &&
      !loadingProposal &&
      proposalPending.current &&
      proposalToken &&
      proposalToken.token
    ) {
      const param = `lenguage=${langToReport(i18n.language)}&token=${proposalToken.token}`;
      window.open(getUrl(window.location, "proposal.html", param));
      proposalPending.current = false;
    }
    prevLoadingProposal.current = loadingProposal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProposal]);

  useEffect(() => {
    if (
      prevLoadingReport.current &&
      !loadingReport &&
      reportPending.current &&
      reportToken &&
      reportToken.token
    ) {
      const param = `lenguage=${langToReport(i18n.language)}&token=${reportToken.token}`;
      window.open(getUrl(window.location, "report.html", param));
      reportPending.current = false;
    }
    prevLoadingReport.current = loadingReport;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingReport]);

  const viewProposal = () => {
    if (!campaign) return;
    proposalPending.current = true;
    getProposalRequest({ campaign_id: campaign.id });
  };

  const viewReport = () => {
    if (!campaign) return;
    const displays = campaign.contents_displays.map((d) => d.display_id);
    const contents = campaign.contents_displays.map((d) => d.content_id);
    reportPending.current = true;
    getReportRequest({
      properties: {
        displays: displays.toString(),
        contents: contents.toString(),
        from_date: campaign.start_date
          ? campaign.start_date.slice(0, 10)
          : "",
        to_date: campaign.end_date ? campaign.end_date.slice(0, 10) : "",
        display_data: 1,
        company: campaign.company_id,
      },
    });
  };

  const goToCMS = () => {
    if (!campaign) return;
    window.open(getCMSLink(token, campaign.id), "_blank", "noopener,noreferrer");
  };

  const isPaused =
    campaign &&
    !campaign.contents_displays.some((cd) => cd.active === 1);

  const togglePause = () => {
    if (!campaign) return;
    const willActivate = isPaused;
    Modal.confirm({
      title: willActivate ? t("Activate campaign") : t("Pause campaign"),
      content: willActivate
        ? t(
            "The campaign will be activated, which means it will start running and begin consuming budget again until it is paused."
          )
        : t(
            "The campaign will be paused, which means it will stop running and will not consume budget until it is reactivated."
          ),
      okText: willActivate ? t("Activate campaign") : t("Pause campaign"),
      cancelText: t("Cancel"),
      okButtonProps: {
        style: {
          background: Colors.primary,
          borderColor: Colors.primary,
          color: Colors.onPrimary,
        },
      },
      onOk: () => {
        const payload = {
          contents_displays: campaign.contents_displays.map((c) => ({
            ...c,
            rules: c.rules && c.rules.length > 0 ? JSON.parse(c.rules) : null,
            display_id: c.display_id,
            active: willActivate ? 1 : 0,
            relation_id: c.id,
          })),
        };
        editCampaign(campaign.id, payload);
      },
    });
  };

  return {
    viewProposal,
    viewReport,
    goToCMS,
    togglePause,
    isPaused,
  };
};

export default useCampaignDetailActions;
